import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ArrowLeft,
  Building2,
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CompanyManagement() {
  const { user, loading: authLoading } = useAuth();
  const { companies, selectCompany } = useCompany();
  const navigate = useNavigate();
  
  const [companyList, setCompanyList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [deletingCompany, setDeletingCompany] = useState(null);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [editCompanyName, setEditCompanyName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/companies`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setCompanyList(data.companies || []);
      } else if (response.status === 403) {
        toast.error('Access denied');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!authLoading && user) {
      if (user.role !== 'manager') {
        toast.error('Access denied');
        navigate('/dashboard');
        return;
      }
      fetchCompanies();
    }
  }, [authLoading, user, fetchCompanies, navigate]);

  const handleAddCompany = async (e) => {
    e.preventDefault();
    if (!newCompanyName.trim()) return;

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/companies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: newCompanyName.trim() })
      });

      if (response.ok) {
        toast.success('Company added successfully');
        setNewCompanyName('');
        setShowAddModal(false);
        fetchCompanies();
        // Refresh company context
        window.location.reload();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add company');
      }
    } catch (error) {
      toast.error('Failed to add company');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditCompany = async (e) => {
    e.preventDefault();
    if (!editCompanyName.trim() || !editingCompany) return;

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/companies/${editingCompany.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: editCompanyName.trim() })
      });

      if (response.ok) {
        toast.success('Company updated successfully');
        setEditingCompany(null);
        setEditCompanyName('');
        fetchCompanies();
        // Refresh company context
        window.location.reload();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update company');
      }
    } catch (error) {
      toast.error('Failed to update company');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCompany = async () => {
    if (!deletingCompany) return;

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/companies/${deletingCompany.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Company deleted successfully');
        setDeletingCompany(null);
        fetchCompanies();
        // Refresh company context
        window.location.reload();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete company');
      }
    } catch (error) {
      toast.error('Failed to delete company');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (company) => {
    setEditingCompany(company);
    setEditCompanyName(company.name);
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="loading-state">
          <div className="loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-content" data-testid="company-management-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="h-10 w-10 p-0"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Company Management</h1>
              <p className="text-slate-500 mt-1">Add, edit, or remove companies</p>
            </div>
          </div>
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="add-company-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Company
          </Button>
        </div>

        {/* Companies List */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="companies-list">
          {companyList.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">No companies found</p>
              <p className="text-sm text-slate-400 mt-1">Add your first company to get started</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-200">
              {companyList.map((company) => (
                <div
                  key={company.id}
                  className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`company-row-${company.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{company.name}</p>
                      <p className="text-xs text-slate-500">ID: {company.id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditModal(company)}
                      className="h-9"
                      data-testid={`edit-company-${company.id}`}
                    >
                      <Pencil className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeletingCompany(company)}
                      className="h-9 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                      data-testid={`delete-company-${company.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add Company Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden" data-testid="add-company-modal">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">Add New Company</h2>
                <button
                  onClick={() => { setShowAddModal(false); setNewCompanyName(''); }}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              <form onSubmit={handleAddCompany}>
                <div className="p-6">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Company Name
                  </label>
                  <Input
                    value={newCompanyName}
                    onChange={(e) => setNewCompanyName(e.target.value)}
                    placeholder="Enter company name"
                    className="h-12"
                    data-testid="new-company-name-input"
                    autoFocus
                  />
                </div>
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setShowAddModal(false); setNewCompanyName(''); }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={!newCompanyName.trim() || submitting}
                    className="bg-slate-900 hover:bg-slate-800"
                    data-testid="submit-add-company"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Company
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Company Modal */}
        {editingCompany && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden" data-testid="edit-company-modal">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">Edit Company</h2>
                <button
                  onClick={() => { setEditingCompany(null); setEditCompanyName(''); }}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              <form onSubmit={handleEditCompany}>
                <div className="p-6">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Company Name
                  </label>
                  <Input
                    value={editCompanyName}
                    onChange={(e) => setEditCompanyName(e.target.value)}
                    placeholder="Enter company name"
                    className="h-12"
                    data-testid="edit-company-name-input"
                    autoFocus
                  />
                </div>
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setEditingCompany(null); setEditCompanyName(''); }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={!editCompanyName.trim() || submitting}
                    className="bg-slate-900 hover:bg-slate-800"
                    data-testid="submit-edit-company"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deletingCompany && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden" data-testid="delete-company-modal">
              <div className="p-6">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <h2 className="text-lg font-bold text-slate-900 text-center mb-2">Delete Company</h2>
                <p className="text-slate-500 text-center">
                  Are you sure you want to delete <strong>{deletingCompany.name}</strong>? 
                  This action cannot be undone.
                </p>
                <p className="text-sm text-amber-600 bg-amber-50 rounded-lg p-3 mt-4 text-center">
                  Note: Companies with existing tickets cannot be deleted.
                </p>
              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
                <Button
                  variant="outline"
                  onClick={() => setDeletingCompany(null)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteCompany}
                  disabled={submitting}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="confirm-delete-company"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Company
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
